import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FeedbackService } from '@services/feedback';
import { ProgressIndicatorService } from '@services/progress';
import { finalize } from 'rxjs/operators';

import { ILocale } from '../../../../../../services/utils/utils.models';
import { UtilsService } from '../../../../../../services/utils/utils.service';
import { FormEditorInputType, ISearchSelectData } from '../../../../../../shared/components';

@Component({
	selector: 'app-legal-app-localisation',
	templateUrl: './legal-app-localisation.component.html',
	styleUrls: ['./legal-app-localisation.component.scss']
})
export class LegalAppLocalisationComponent implements OnInit, AfterViewInit {
	@Input() formInputs: FormGroup;
	@Input() displayNames: { [locale: string]: string } = {};

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	public formEditorInputType = FormEditorInputType;
	public locales: ILocale[] = [];
	public availableLocales: ISearchSelectData[] = [];
	public tableColumns: string[] = ['language', 'id', 'name', 'action'];
	public tableDataSource = new MatTableDataSource([]);
	public isLocalised = true;

	constructor(
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		private utilsService: UtilsService,
		private formBuilder: FormBuilder
	) {}

	public ngOnInit(): void {
		this.progressIndicatorService.spinner(true);

		this.isLocalised = [...Object.keys(this.displayNames ? this.displayNames : {})].length > 0;

		this.getLocales();
	}

	public ngAfterViewInit(): void {
		this.tableDataSource.sort = this.sort;
		this.tableDataSource.paginator = this.paginator;
	}

	public addLocale(locale: string): void {
		(this.formInputs.get('localisation') as FormGroup).addControl(
			locale,
			this.formBuilder.group({
				displayName: ['']
			})
		);

		this.tableDataSource.data = [
			...this.tableDataSource.data,
			{
				id: locale,
				language: this.locales.find((l) => l.code === locale).name,
				displayName: ''
			}
		];

		this.availableLocales = this.availableLocales.filter((l) => l.id !== locale);
	}

	public removeLocale(locale: string): void {
		(this.formInputs.get('localisation') as FormGroup).removeControl(locale);
		this.tableDataSource.data = [...this.tableDataSource.data.filter((item) => item.id !== locale)];

		const availableLocale = this.locales.find((l) => l.code === locale);

		if (availableLocale) {
			this.availableLocales.push({
				id: availableLocale.code,
				name: `${availableLocale.name} (${availableLocale.code})`
			});
		}
	}

	public search(event: Event): void {
		const filterValue = (event.target as HTMLInputElement).value;
		this.tableDataSource.filter = filterValue.trim().toLowerCase();
	}

	private createDataSource(): void {
		const localisations: { [locale: string]: { id: string; language: string; displayName: string } } = {};

		for (const [key, value] of Object.entries(this.displayNames ? this.displayNames : {})) {
			const locale = this.locales.find((l) => l.code === key);
			const name = locale?.name ? locale.name : '-';

			if (localisations[key]) {
				localisations[key].id = key;
				localisations[key].language = name;
				localisations[key].displayName = value;
			} else {
				localisations[key] = {
					id: key,
					language: name,
					displayName: value
				};
			}
		}

		Object.entries(localisations).forEach(([key, value]) =>
			(this.formInputs.get('localisation') as FormGroup).addControl(
				key,
				this.formBuilder.group({
					displayName: [value.displayName]
				})
			)
		);

		this.tableDataSource.data = Object.values(localisations);
	}

	private getLocales(): void {
		this.utilsService
			.getLocales()
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe({
				next: (res) => {
					this.locales = res;

					this.availableLocales = res
						.filter((locale) => [...Object.keys(this.displayNames ? this.displayNames : {})].indexOf(locale.code) === -1)
						.map((locale) => {
							return {
								id: locale.code,
								name: `${locale.name} (${locale.code})`
							};
						});

					this.createDataSource();
				},
				error: (err) => this.feedbackService.apiError(err)
			});
	}
}
